import {
  IonHeader,
  IonToolbar,
  IonButtons,
  IonBadge,
  IonAvatar,
  IonIcon,
} from "@ionic/react";
import { arrowBack } from "ionicons/icons";
import { useSelector } from "react-redux";
import avatarDefault from "../../../assets/avatars/avatarDefault.png";
import dsCoinImage from "../../../assets/images/ds-coin.png";

import { useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router";
import config from "../../../settings/config";
import { resolveUserAvatarSrc } from "../../../services/functions";
import dietaSocialLogo from "../../../assets/images/ds-logo.svg";
import dietaSocialLogoSandbox from "../../../assets/images/ds-logo-sandbox.svg";
import { Icon } from "@iconify/react/dist/iconify.js";
import { safeNavigate } from "../../../utils/navigationHelper";
import { selectTotalUnread } from "../../../redux/reducer/chatUnreadSlice";
import { selectMemberPlan } from "../../../redux/selectors/plan";
import { ownPlanBadgeName } from "../../../utils/planBadge";
import "./MainHeader.min.css";

const MainHeader = ({
  title,
  href,
  forceHref = false,
  hrefParams = {},
  onBack,
  CustomComponent,
  appNotificationsEnabled = null,
  backButton = false,
}) => {
  const logo = config.env.mode !== "prod" && config.env.mode !== "preprod" ? dietaSocialLogoSandbox : dietaSocialLogo;

  const location = useLocation();
  const user = useSelector((state) => state.user?.user);
  const memberPlan = useSelector(selectMemberPlan);
  const avatar = useSelector((state) => state.user?.user?.avatar);
  const coinBalance = useSelector((state) => state.user?.coins?.balance);
  const history = useHistory();

  const storage_path = useSelector((state) => state.user?.user?.storage_path);

  const avatarSrc =
    resolveUserAvatarSrc({ avatar, storage_path }) || avatarDefault;

  const subscription =
    useSelector((state) => state.user?.master_club_subscriber?.subscription) ??
    null;
  const adocPatient = useSelector((state) => state.user?.adoc_patient);
  // Set by the backend only while the effective plan mode is Adoc.
  const adocBadge = adocPatient?.badge ?? null;

  const unreadNotificationsList = useSelector(
    (state) => state.notifications.getNotificationListUnread,
  );
  const recipientsUnreadCount = useSelector(
    (state) => state.notifications.recipientsUnreadCount,
  );
  const totalChatUnread = useSelector(selectTotalUnread);

  // Active Adoc plan → show ADOC instead of guest/premium subscription name.
  const subscriptionType =
    adocBadge?.name ?? subscription?.subscription_type?.name ?? "Guest";
  // Premium Lite is type "premium" (same app behaviour as a paying member);
  // only the badge says so: «LITE» (client, 23/10/2026).
  const badgeLabel = ownPlanBadgeName(subscriptionType, memberPlan, {
    hasOwnBadge: !!adocBadge?.name,
  });
  const subscriptionColor =
    adocBadge?.color ?? subscription?.subscription_type?.color ?? "light";
  // Adoc members get premium-like app behaviour (permissions come from BE).
  const isPremium = subscriptionType === "premium" || !!adocBadge;

  useEffect(() => {
    if (!user) return;
    if (localStorage.getItem("adoc_app_n") === null)
      localStorage.setItem("adoc_app_n", true);
  }, []);

  const disabledByPremium =
    isPremium && JSON.parse(localStorage.getItem("adoc_app_n")) === false;

  const showAppNotifications =
    appNotificationsEnabled !== null
      ? appNotificationsEnabled
      : !disabledByPremium;

  const dismissClosestModal = () => {
    // Finds the closest ion-modal hosting this header and dismisses it
    const el = document.getElementById("main-header");
    const modal = el?.closest("ion-modal");
    if (modal && typeof modal.dismiss === "function") {
      modal.dismiss();
      return true;
    }
    return false;
  };

  const handleBack = () => {
    if (typeof onBack === "function") {
      onBack();
      return;
    }
    if (dismissClosestModal()) return;

    // Prefer a real pop when there's a prior entry to return to.
    // Always pushing the href grows the stack; Ionic's outlet then
    // pops the next ← tap, producing a ping-pong loop between the
    // current page and its href target. Only fall back to the
    // explicit href for cold entries (deep link / hard refresh).
    const canPop = history.length > 1 && history.action !== "POP";
    if (canPop) {
      history.goBack();
      return;
    }
    if (href) {
      safeNavigate(history, href, location.pathname);
    } else {
      history.goBack();
    }
  };

  const handleMenuClick = () => {
    const menu = document.querySelector("ion-menu#main-menu");
    if (menu) {
      menu.toggle();
    }
  };

  const handleNavigation = (path) => {
    safeNavigate(history, path, location.pathname);
  };

  // Navigate to Dieta Social profile when logo is clicked
  const handleLogoClick = () => {
    // Navigate to Dieta Social profile page
    // The Dieta Social user ID is fetched from API when needed
    // For now we use a special route that the SocialProfile page can handle
    safeNavigate(history, "/dieta-social-profile", location.pathname);
  };

  return (
    <IonHeader id="main-header">
      <IonToolbar>
        <IonButtons slot="start">
          <div className="header-start">
            {/* Back/Menu Button */}
            {href || backButton ? (
              <div
                className="back-button"
                onClick={
                  href && forceHref
                    ? () => history.replace(href, hrefParams)
                    : handleBack
                }
              >
                <IonIcon icon={arrowBack} className="back-icon" />
              </div>
            ) : (
              <div className="menu-button" onClick={handleMenuClick}>
                <Icon icon="ci:menu-alt-03" className="menu-icon" />
              </div>
            )}

            {/* Logo - clickable to go to Dieta Social profile */}
            <img 
              src={logo} 
              alt="Dieta Social Logo" 
              className="logo" 
              onClick={handleLogoClick}
              style={{ cursor: "pointer" }}
            />
          </div>
        </IonButtons>

        <IonButtons slot="end">
          <div className="header-end">
            {/* Chat */}
            <div
              className="header-item header-item-chat"
              onClick={() => handleNavigation("/chat")}
            >
              <div className="icon-wrapper">
                <Icon icon="ri:chat-3-fill" className="icon" />
                {totalChatUnread > 0 && (
                  <IonBadge color="danger" className="notification-badge">
                    {totalChatUnread > 99 ? "99+" : totalChatUnread}
                  </IonBadge>
                )}
              </div>
              <span className="label">Chat</span>
            </div>

            {/* Coins */}
            <div
              className="header-item header-item-coins"
              onClick={() => handleNavigation("/saldo-monete")}
            >
              <div className="icon-wrapper">
                <img src={dsCoinImage} alt="Coins" className="coin-image" />
              </div>
              <span className="label">{coinBalance ?? 0}</span>
            </div>

            {/* Notifications */}
            {showAppNotifications && (
              <div
                className="header-item"
                onClick={() => handleNavigation("/notifiche")}
              >
                <div className="icon-wrapper">
                  <Icon icon="ri:notification-fill" className="icon" />
                  {(recipientsUnreadCount > 0 || !!unreadNotificationsList.quantity) && (
                    <IonBadge color="danger" className="notification-badge">
                      {recipientsUnreadCount > 0
                        ? recipientsUnreadCount
                        : unreadNotificationsList.quantity}
                    </IonBadge>
                  )}
                </div>
                <span className="label">Notifiche</span>
              </div>
            )}

            {/* Avatar/Profile */}
            <div
              className="avatar-item"
              onClick={() => handleNavigation("/me")}
            >
              <IonAvatar>
                <img src={avatarSrc} alt="Profile" />
              </IonAvatar>
              <span
                className={`avatar-badge badge-${subscriptionColor}`}
                style={subscriptionColor?.startsWith("#") ? { backgroundColor: subscriptionColor } : undefined}
              >
                {badgeLabel.toUpperCase()}
              </span>
            </div>
          </div>
        </IonButtons>
      </IonToolbar>
    </IonHeader>
  );
};

export default MainHeader;
